import type { Source } from "../../shared/types.ts";
import ashbyMark from "../brand/integrations/ashby-mark.png";
import ashbyWord from "../brand/integrations/ashby-wordmark.svg";
import bamboohrMark from "../brand/integrations/bamboohr-mark.png";
import bamboohrWord from "../brand/integrations/bamboohr-wordmark.svg";
import greenhouseMark from "../brand/integrations/greenhouse-mark.png";
import greenhouseWord from "../brand/integrations/greenhouse-wordmark.svg";
import jazzhrMark from "../brand/integrations/jazzhr-mark.png";
import jazzhrWord from "../brand/integrations/jazzhr-wordmark.svg";
import leverMark from "../brand/integrations/lever-mark.png";
import leverWord from "../brand/integrations/lever-wordmark.png";
import recruiteeMark from "../brand/integrations/recruitee-mark.png";
import recruiteeWord from "../brand/integrations/recruitee-wordmark.png";
import smartrecruitersMark from "../brand/integrations/smartrecruiters-mark.png";
import smartrecruitersWord from "../brand/integrations/smartrecruiters-wordmark.svg";
import workableMark from "../brand/integrations/workable-mark.png";
import workableWord from "../brand/integrations/workable-wordmark.svg";
import workdayMark from "../brand/integrations/workday-mark.png";
import workdayWord from "../brand/integrations/workday-wordmark.svg";
import { cn } from "./cn";

export const SOURCE_NAMES: Record<Source, string> = {
  greenhouse: "Greenhouse",
  lever: "Lever",
  ashby: "Ashby",
  workable: "Workable",
  recruitee: "Recruitee",
  bamboohr: "BambooHR",
  jazzhr: "JazzHR",
  workday: "Workday",
  smartrecruiters: "SmartRecruiters",
};

/** Every source, in the order the landing lists them. */
export const SOURCES_SHOWN = Object.keys(SOURCE_NAMES) as Source[];

/** Each ATS's own square app mark and wordmark, downloaded once from the vendor and bundled. */
export const SOURCE_LOGOS: Record<Source, { mark: string; wordmark: string }> = {
  greenhouse: { mark: greenhouseMark, wordmark: greenhouseWord },
  lever: { mark: leverMark, wordmark: leverWord },
  ashby: { mark: ashbyMark, wordmark: ashbyWord },
  workable: { mark: workableMark, wordmark: workableWord },
  recruitee: { mark: recruiteeMark, wordmark: recruiteeWord },
  bamboohr: { mark: bamboohrMark, wordmark: bamboohrWord },
  jazzhr: { mark: jazzhrMark, wordmark: jazzhrWord },
  workday: { mark: workdayMark, wordmark: workdayWord },
  smartrecruiters: { mark: smartrecruitersMark, wordmark: smartrecruitersWord },
};

/** A source key from a key or a display name in any case, e.g. "Greenhouse" or "bamboohr", or null for anything else. */
export function sourceOf(s: string | null | undefined): Source | null {
  const k = (s ?? "").toLowerCase().replace(/[^a-z]/g, "");
  return Object.hasOwn(SOURCE_NAMES, k) ? (k as Source) : null;
}

const marks = { xs: "size-4 rounded-[3px]", sm: "size-5 rounded-[4px]", md: "size-7 rounded-[5px]", lg: "size-10 rounded-[7px]" };
const words = { xs: "h-3.5", sm: "h-4", md: "h-6", lg: "h-8" };
const labels = { xs: "text-[12px]", sm: "text-[13px]", md: "text-[15px]", lg: "text-[17px]" };
const px = { xs: 16, sm: 20, md: 28, lg: 40 };

/**
 * An applicant tracking system's real logo. `variant="mark"`, the default, is the square app icon, with the name beside
 * it when `withName` is set. `variant="wordmark"` is the vendor's full logotype at the size's height. Unknown sources
 * render nothing rather than a stand-in.
 */
export function SourceLogo({
  source,
  size = "sm",
  variant = "mark",
  withName = false,
  className,
}: {
  source: Source | string;
  size?: "xs" | "sm" | "md" | "lg";
  variant?: "mark" | "wordmark";
  withName?: boolean;
  className?: string;
}) {
  const key = sourceOf(source);
  if (!key) return null;
  const name = SOURCE_NAMES[key];

  if (variant === "wordmark") {
    return <img src={SOURCE_LOGOS[key].wordmark} alt={name} decoding="async" draggable={false} className={cn("w-auto max-w-none shrink-0 select-none", words[size], className)} />;
  }

  const mark = (
    <img
      src={SOURCE_LOGOS[key].mark}
      alt={withName ? "" : name}
      width={px[size]}
      height={px[size]}
      decoding="async"
      draggable={false}
      className={cn("shrink-0 select-none object-cover", marks[size], !withName && className)}
    />
  );
  if (!withName) return mark;
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-2 font-semibold text-ink", labels[size], className)}>
      {mark}
      {name}
    </span>
  );
}
